import { apiBaseUrl } from '../lib/env.js';
import { useSession } from '../state/session.js';
import { IS_DEMO } from '../demo/config.js';
import { mockRequest } from '../demo/api.js';

interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  readonly status: number;

  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  auth?: boolean;
  /** Skip the auto-refresh dance; used internally by the refresh call itself. */
  skipRefresh?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(path, apiBaseUrl);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function rawRequest<T>(path: string, opts: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const token = opts.auth === false ? null : useSession.getState().accessToken;
  if (token) headers.authorization = `Bearer ${token}`;

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const body = json as ApiErrorBody;
    throw new ApiError(res.status, body.error?.code ?? 'unknown', body.error?.message ?? res.statusText);
  }
  return (json as { data: T }).data;
}

async function tryRefresh(): Promise<boolean> {
  const { refreshToken, setTokens, clear } = useSession.getState();
  if (!refreshToken) return false;
  try {
    const tokens = await rawRequest<{ accessToken: string; refreshToken: string }>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
      auth: false,
    });
    setTokens(tokens);
    return true;
  } catch {
    clear();
    return false;
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  if (IS_DEMO) {
    try {
      return await mockRequest<T>(path, opts);
    } catch (err) {
      throw new ApiError(400, 'demo_error', err instanceof Error ? err.message : 'Demo error.');
    }
  }
  try {
    return await rawRequest<T>(path, opts);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && opts.skipRefresh !== true && opts.auth !== false) {
      const refreshed = await tryRefresh();
      if (refreshed) return rawRequest<T>(path, opts);
    }
    throw err;
  }
}
